import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useOfferData } from "../../hooks/useOfferData";
import { useSearchStore } from "../../hooks/useSearchStore";
import { passions } from "./constants";
import Search from "./Search";
import Card from "./Card/Card";
import CardSkeleton from "./Card/CardSkeleton";
import ArrowRightIcon from "../../assets/offers/arrow-right.png";
import RightChevronCircle from "../../assets/offers/chevron-down.png";
import GoogleMap from "./GoogleMap/GoogleMap";

import styles from "./OffersLandingPage.module.css";
import Modal from "./Modal";
import OfferDetailsContent from "./Modal/OfferDetailsContent";
import MoreFiltersModal from "./Modal/MoreFiltersModal";
import placeholderImage from "../../assets/images/img_placeholder.jpg";
import MapIcon from "../../assets/offers/no_property_icon.png";
import { useMatchBreakpoint } from "../../hooks/useMatchBreakpoint";
import classNames from "classnames";
import { ENV } from "../../env";

// Helper function to parse filters from URL search params
const parseFiltersFromSearchParams = (searchParams) => {
  const filters = {};

  const passionsParam = searchParams.get("passions");
  if (passionsParam) {
    filters.passions = passionsParam.split(",").filter(Boolean);
  }

  const luxuryBrandsParam = searchParams.get("luxury_brands");
  if (luxuryBrandsParam) {
    filters.luxuryBrands = luxuryBrandsParam.split(",").filter(Boolean);
  }

  const premiumBrandsParam = searchParams.get("premium_brands");
  if (premiumBrandsParam) {
    filters.premiumBrands = premiumBrandsParam.split(",").filter(Boolean);
  }

  return filters;
};

export const OffersLandingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const offerId = searchParams.get("offerId");
  const passionParam = searchParams.get("passion") || "golf";
  const placeIdParam = searchParams.get("place_id");
  const placeNameParam = searchParams.get("place_name");
  const [shouldSkipInitialRequest, setShouldSkipInitialRequest] = useState(
    placeIdParam && placeNameParam,
  );
  const passion = passionParam ? passions[passionParam] : passions.golf;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMoreFiltersModalOpen, setIsMoreFiltersModalOpen] = useState(false);

  const [selectedFilters, setSelectedFilters] = useState(
    parseFiltersFromSearchParams(searchParams),
  );
  const [selectedOfferId, setSelectedOfferId] = useState(offerId || "");
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  // Update filters when URL parameters change (browser back/forward, direct URL edit)
  useEffect(() => {
    const filtersFromUrl = parseFiltersFromSearchParams(searchParams);
    setSelectedFilters(filtersFromUrl);
  }, [searchParams]);
  const [isSearchingByLocation, setIsSearchingByLocation] = useState(false);
  const [lastSearchParams, setLastSearchParams] = useState(null);
  const heroSectionRef = useRef(null);
  const offersContentRef = useRef(null);
  const mapRef = useRef(null);
  const isMobile = useMatchBreakpoint(800);
  const mobileSearchRef = useRef(null);
  const mobileControlsRef = useRef(null);

  const {
    offerData,
    basicDataLoading,
    propertiesLoading,
    handleSearchChange: originalHandleSearchChange,
    bookNow,
    fetchOfferData,
    loadMoreProperties,
    loadMoreLocationProperties,
    hasMoreData,
    wasSearchPerformed,
    setWasSearchPerformed,
  } = useOfferData(selectedOfferId, passionParam, shouldSkipInitialRequest);

  const showSkeleton =
    wasSearchPerformed &&
    propertiesLoading &&
    !offerData?.hotProperties?.hotels?.length;
  const showHotels =
    wasSearchPerformed &&
    !showSkeleton &&
    offerData?.hotProperties?.hotels?.length > 0;
  const showNoProperties = wasSearchPerformed && !showSkeleton && !showHotels;

  // Wrapper function to track location-based searches
  const handleSearchChange = async (params) => {
    // Check if this is a location-based search
    const hasLocation =
      params.selectedLocation &&
      (params.selectedLocation.latitude ||
        params.selectedLocation.longitude ||
        params.selectedLocation.city ||
        params.selectedLocation.state ||
        params.selectedLocation.country);
    setIsSearchingByLocation(hasLocation);

    // Store search params for pagination
    if (hasLocation) {
      setLastSearchParams(params);
      // Call the original search function
      return await originalHandleSearchChange(params);
    } else {
      return await wrappedFetchOfferData();
    }
  };

  // Wrapper function to track when fetching default offer data (non-location search)
  const wrappedFetchOfferData = async () => {
    // This is not a location-based search, so enable pagination
    setIsSearchingByLocation(false);
    setWasSearchPerformed(true);

    // Call the original fetch function
    return await fetchOfferData();
  };

  // Handle search by location from map
  const handleSearchByLocation = async (locationData) => {
    const { center, state } = locationData;

    // Get current search parameters from the store
    const { rooms, guests, children } = useSearchStore.getState();
    const latitude = center.lat;
    const longitude = center.lng;
    // Create search parameters in the format expected by handleSearchChange
    const searchParams = {
      selectedLocation: {
        city: "", // We don't have city from the map center
        state: state,
        country: "US", // Assuming US for now
      },
      rooms: rooms,
      guests: guests,
      children: children,
      placeDetails: {
        place_id: null,
        name: `${state}, US`,
        formatted_address: `${state}, US`,
      },
    };
    if (latitude && longitude && !state) {
      searchParams.selectedLocation.latitude = latitude;
      searchParams.selectedLocation.longitude = longitude;
    }
    return await handleSearchChange(searchParams);
  };

  const handleSeeOfferDetails = () => {
    setIsModalOpen(true);
  };

  const handleMoreFilters = () => {
    setIsMoreFiltersModalOpen(true);
  };

  // Helper function to update URL with filters
  const updateUrlWithFilters = (filters) => {
    const params = new URLSearchParams(searchParams);

    // Remove existing filter params
    params.delete("passions");
    params.delete("luxury_brands");
    params.delete("premium_brands");

    // Add new filter params if they exist
    if (filters.passions && filters.passions.length > 0) {
      params.set("passions", filters.passions.join(","));
    }

    if (filters.luxuryBrands && filters.luxuryBrands.length > 0) {
      params.set("luxury_brands", filters.luxuryBrands.join(","));
    }

    if (filters.premiumBrands && filters.premiumBrands.length > 0) {
      params.set("premium_brands", filters.premiumBrands.join(","));
    }

    setSearchParams(params);
  };

  const handleApplyFilters = (filters) => {
    setSelectedFilters(filters);
    updateUrlWithFilters(filters);
  };

  const handleFiltersChange = (filters) => {
    setSelectedFilters(filters);
    updateUrlWithFilters(filters);
  };

  const handleMarkerClick = (hotel) => {
    // Find the card element with the matching hotel ID
    const cardElement = document.querySelector(`[data-hotel-id="${hotel.id}"]`);
    if (cardElement) {
      // Scroll the card into view with smooth behavior
      cardElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });

      // Optional: Add a brief highlight effect
      cardElement.style.transition = "box-shadow 0.3s ease";
      cardElement.style.boxShadow = "0 0 20px rgba(0, 123, 255, 0.5)";
      setTimeout(() => {
        cardElement.style.boxShadow = "";
      }, 2000);
    }
  };

  const handleCardClick = (hotel) => {
    // Trigger the map to center on the corresponding marker
    if (mapRef.current && mapRef.current.centerOnHotel) {
      mapRef.current.centerOnHotel(hotel);
    }
  };

  useEffect(() => {
    if (offerId) {
      setSelectedOfferId(offerId);
    }
  }, [offerId]);

  // Close mobile search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click was on the mobile controls button or its children
      if (
        mobileControlsRef.current &&
        (mobileControlsRef.current === event.target ||
          mobileControlsRef.current.contains(event.target))
      ) {
        // Don't do anything if the click was on the mobile controls button
        return;
      }

      // Otherwise, if the click was outside the mobile search panel, close it
      if (
        mobileSearchRef.current &&
        !mobileSearchRef.current.contains(event.target)
      ) {
        setShowMobileSearch(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Infinite scroll effect
  useEffect(() => {
    const handleScroll = () => {
      // Check if we're near the bottom of the page
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Trigger load more when user is within 200px of the bottom
      const isNearBottom = scrollTop + windowHeight >= documentHeight - 200;

      if (isNearBottom && hasMoreData && !propertiesLoading) {
        console.log("Triggering infinite scroll load more");
        if (isSearchingByLocation && lastSearchParams) {
          // Load more location-based properties
          loadMoreLocationProperties(lastSearchParams);
        } else {
          // Load more ID-based properties
          loadMoreProperties();
        }
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [
    hasMoreData,
    propertiesLoading,
    loadMoreProperties,
    loadMoreLocationProperties,
    isSearchingByLocation,
    lastSearchParams,
  ]);

  const renderSkeletons = () =>
    Array(6)
      .fill()
      .map((_, index) => <CardSkeleton key={`skeleton-${index}`} />);

  const renderHotels = () => (
    <>
      {offerData.hotProperties.hotels.map((property) => (
        <Card
          key={property.id}
          title={property.name}
          description={property.description}
          image={
            property.imageUrl
              ? `https://cache.marriott.com/${property.imageUrl}`
              : placeholderImage
          }
          rating={property.reviews.rating || ""}
          reviewCount={property.reviews.count || "0"}
          icon={passion?.icon}
          chip={passion?.chip}
          isPassion={property.isPassion}
          price={
            property.rate?.lowestAverageNightlyRate?.value ||
            property.rate?.firstNightLowestStay?.value ||
            ""
          }
          fee={
            property.rate?.lowestAverageNightlyRateFeeAmount ||
            property.rate?.firstNightLowestStayFeeAmount ||
            0
          }
          bookNow={bookNow}
          propertyData={property}
          onClick={() => handleCardClick(property)}
        />
      ))}
      {/* Loading indicator for pagination */}
      {propertiesLoading && (
        <div className={styles.paginationLoading}>
          <div className={styles.paginationLoadingSpinner}></div>
          <span>Loading more hotels...</span>
        </div>
      )}
    </>
  );

  const renderNoProperties = () => (
    <div className={styles.noPropertiesContainer}>
      <div className={styles.noPropertiesContent}>
        <img
          className={styles.noPropertiesImage}
          src={MapIcon}
          alt="No Properties Found"
        />
        <h3 className={styles.noPropertiesTitle}>
          We couldn’t find any properties that match your search.
        </h3>
        <p className={styles.noPropertiesSuggestion}>
          Try tweaking the search criteria to find the perfect spot for your
          next stay.
        </p>
      </div>
    </div>
  );

  if (!passion) {
    window.location.href = "https://www.marriott.com/offers.mi";

    return null;
  }

  return (
    <div className={styles.offersContainer}>
      <div
        ref={heroSectionRef}
        className={styles.heroSection}
        style={{ "--bg": `url(${passion.image})` }}
      >
        <div className={styles.heroOverlay}>
          <img className={styles.passionIcon} src={passion.icon} alt="Golf" />
          <div className={styles.divider}></div>
          <div
            className={classNames(
              styles.heroContent,
              offerData?.id
                ? styles.loaded
                : basicDataLoading
                  ? styles.loading
                  : "",
            )}
          >
            <div>
              <h2 className={styles.passionHeading} data-testid="offers-heading">
                {offerData?.id
                  ? `${passionParam?.charAt(0)?.toUpperCase() + passionParam?.slice(1)} ${offerData?.title}` ||
                    "[ NO SUBTITLE ]"
                  : ""}
              </h2>
            </div>
            <p className={styles.passionDescription}>
              {offerData?.id
                ? passion.passionDescription || "[ NO DESCRIPTION ]"
                : ""}
            </p>
          </div>
          <button
            data-testid="offers-details"
            onClick={() => handleSeeOfferDetails()}
            className={classNames(
              styles.passionLinkButton,
              "custom_click_track",
            )}
            data-custom_click_track_value={`Offer Details|Offer Details Link|internal|category=OFFER,product=${offerId}`}
          >
            {passion.buttonLink} <img src={ArrowRightIcon} alt="Go" />
          </button>
        </div>
        {!isMobile && (
          <Search
            handleSearchChange={handleSearchChange}
            offerData={offerData}
            fetchOfferData={wrappedFetchOfferData}
            propertiesLoading={propertiesLoading}
            setSkipInitialization={setShouldSkipInitialRequest}
            onMoreFilters={handleMoreFilters}
            selectedFilters={selectedFilters}
            onFiltersChange={handleFiltersChange}
          />
        )}
      </div>
      {isMobile && (
        <>
          <div
            ref={mobileControlsRef}
            className={styles.mobileControls}
            onClick={() => setShowMobileSearch((prevState) => !prevState)}
          >
            <span>Explore Participating Properties</span>
            <span>
              {showMobileSearch ? "HIDE" : "CUSTOMIZE"}{" "}
              <img
                src={RightChevronCircle}
                alt="Customize"
                className={showMobileSearch ? styles.rotateIcon : ""}
              />
            </span>
          </div>
          {showMobileSearch && (
            <div className={styles.searchWrapper} ref={mobileSearchRef}>
              <Search
                handleSearchChange={handleSearchChange}
                offerData={offerData}
                fetchOfferData={wrappedFetchOfferData}
                propertiesLoading={propertiesLoading}
                setSkipInitialization={setShouldSkipInitialRequest}
                onMoreFilters={handleMoreFilters}
                selectedFilters={selectedFilters}
                onFiltersChange={handleFiltersChange}
              />
            </div>
          )}
        </>
      )}
      <div ref={offersContentRef} className={styles.offersContent}>
        <div
          className={classNames(
            styles.offersList,
            ENV.FEATURE_FLAGS.isMapAvailable ? "" : styles.offersListFullWidth,
          )}
        >
          {showSkeleton ? renderSkeletons() : null}
          {showHotels ? renderHotels() : null}
          {showNoProperties ? renderNoProperties() : null}
        </div>
        {ENV.FEATURE_FLAGS.isMapAvailable && (
          <GoogleMap
            ref={mapRef}
            heroSectionRef={heroSectionRef}
            offersContentRef={offersContentRef}
            hotels={offerData?.hotProperties?.hotels || []}
            className={styles.offersMap}
            passion={passionParam}
            onMarkerClick={handleMarkerClick}
            onSearchByLocation={handleSearchByLocation}
          />
        )}
      </div>
      <Modal
        data-testid="offers-details-dialog"
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Offer Details"
      >
        <OfferDetailsContent offerData={offerData} />
      </Modal>
      <MoreFiltersModal
        isOpen={isMoreFiltersModalOpen}
        onClose={() => setIsMoreFiltersModalOpen(false)}
        onApplyFilters={handleApplyFilters}
        initialFilters={selectedFilters}
      />
    </div>
  );
};
